import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Package, Check, Maximize2, Minimize2, X, Loader2 } from 'lucide-react';
import { sendMessage, getCachedMessages, cacheMessages, parseSystemMessage, getMessages, subscribeToMessages } from '../utils/messages';
import { parseAdShareMessage } from '../utils/adShareMessage';
import AdShareCard from './AdShareCard';
import { getCachedUser } from '../utils/auth';
import { isConversationChatLocked, isOrderOpenForChat, sameOrderParticipants } from '../utils/conversationChat';
import { subscribeToTyping, broadcastTyping } from '../utils/typing';
import { MessageSkeleton } from './common/Skeleton';
import '../styles/ChatWindow.css';

const ChatWindow = ({ conversation, messages: messagesProp, messagesLoading = false, onClose, embedded = false }) => {
  const parentManaged = messagesProp !== undefined;
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [messages, setMessages] = useState(messagesProp || []);
  const [newMessage, setNewMessage] = useState('');
  const [selfLoading, setSelfLoading] = useState(!parentManaged);
  const loading = parentManaged ? messagesLoading : selfLoading;
  const [sending, setSending] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [order, setOrder] = useState(null);
  const [hasActiveOrder, setHasActiveOrder] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const currentUser = getCachedUser();

  const resolveUserById = useCallback((userId) => {
    if (!userId) return null;
    if (currentUser?.id && userId === currentUser.id) return currentUser;
    if (conversation?.user?.id && userId === conversation.user.id) return conversation.user;
    if (conversation?.seller?.id && userId === conversation.seller.id) return conversation.seller;
    if (conversation?.user_id && userId === conversation.user_id) return conversation.user || null;
    if (conversation?.seller_id && userId === conversation.seller_id) return conversation.seller || null;
    return null;
  }, [currentUser, conversation]);

  // Check if user is at bottom of messages
  const checkIfAtBottom = useCallback(() => {
    if (!messagesContainerRef.current) return false;
    const container = messagesContainerRef.current;
    const threshold = 100; // 100px threshold
    return container.scrollHeight - container.scrollTop - container.clientHeight < threshold;
  }, []);

  // Handle scroll events - prevent auto-scroll when user is manually scrolling
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    let scrollTimeout = null;
    let isUserScrolling = false;
    let rafId = null;

    const handleScroll = () => {
      // Cancel previous RAF if still pending
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      
      rafId = requestAnimationFrame(() => {
        setIsAtBottom(checkIfAtBottom());
        
        // Mark that user is actively scrolling
        isUserScrolling = true;
        
        // Clear existing timeout
        if (scrollTimeout) {
          clearTimeout(scrollTimeout);
        }
        
        // Reset flag after user stops scrolling for 1 second
        scrollTimeout = setTimeout(() => {
          isUserScrolling = false;
        }, 1000);
      });
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      container.removeEventListener('scroll', handleScroll);
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [checkIfAtBottom]);

  // Use ref to track last message ID for polling
  const lastMessageIdRef = useRef(null);
  
  // Parent is the source of truth, but keep a just-sent message that props
  // have not caught up with yet so send does not flicker away.
  useEffect(() => {
    if (!parentManaged) return;
    setMessages(Array.isArray(messagesProp) ? messagesProp : []);
  }, [conversation?.id, parentManaged]);

  useEffect(() => {
    if (!parentManaged) return;
    setMessages((current) => {
      const incoming = Array.isArray(messagesProp) ? messagesProp : [];
      if (incoming.length === 0 && current.length > 0 && messagesLoading) return current;
      const byId = new Map(incoming.map((item) => [item.id, item]));
      current.forEach((item) => {
        if (item?.id && !byId.has(item.id)) byId.set(item.id, item);
      });
      return Array.from(byId.values()).sort(
        (a, b) => new Date(a.created_at) - new Date(b.created_at)
      );
    });
  }, [messagesProp, messagesLoading, parentManaged]);

  useEffect(() => {
    if (parentManaged || !conversation?.id) return undefined;
    let cancelled = false;
    setSelfLoading(true);
    getMessages(conversation.id, false).then(({ data }) => {
      if (cancelled) return;
      setMessages(data || []);
      setSelfLoading(false);
    });
    const unsubscribe = subscribeToMessages(conversation.id, (incoming) => {
      setMessages((prev) => (prev.some((item) => item.id === incoming.id) ? prev : [...prev, incoming]));
    });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [conversation?.id, parentManaged]);

  // If user is already at bottom, keep them pinned when new messages arrive (incoming).
  // If user scrolled up, do NOT auto-scroll.
  useEffect(() => {
    if (!messagesContainerRef.current) return;
    if (!isAtBottom) return;
    requestAnimationFrame(() => {
      const el = messagesContainerRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    });
  }, [messages.length, isAtBottom]);

  // Load order info when conversation changes
  useEffect(() => {
    if (!conversation?.id) return;
    if (conversation.request_id) {
      setOrder(null);
      setHasActiveOrder(true);
      return;
    }
    
      const token = localStorage.getItem('auth_token');
    if (!token) return;
      
    const loadOrderInfo = async () => {
        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 8000);

          const ordersResponse = await fetch(`/api/orders?filter=all`, {
            headers: { 'Authorization': `Bearer ${token}` },
            signal: controller.signal,
          });

          clearTimeout(timeoutId);

        if (!ordersResponse.ok) return;

          const ordersResult = await ordersResponse.json();
          if (ordersResult.data) {
          const relatedOrder = ordersResult.data.find(o => o.conversation_id === conversation.id);
            if (relatedOrder) {
              setOrder(relatedOrder);
              const hasOpenOrder = ordersResult.data.some((o) =>
                sameOrderParticipants(o, relatedOrder) && isOrderOpenForChat(o)
              );
              setHasActiveOrder(hasOpenOrder);
            } else {
              setOrder(null);
              setHasActiveOrder(false);
            }
          }
        } catch (err) {
          if (err.name !== 'AbortError') {
          console.error('Error checking order:', err);
          }
        }
    };

    loadOrderInfo();
  }, [conversation?.id, conversation?.request_id]);

  // Subscribe to typing indicators only (messages subscription is in MessagesPage)
  useEffect(() => {
    if (!conversation?.id || !currentUser?.id) return;
    
    const unsubscribeTyping = subscribeToTyping(
      conversation.id,
      currentUser?.id,
      (otherUserTyping) => {
          setIsTyping(otherUserTyping);
      }
    );
    
    return () => {
      if (unsubscribeTyping) unsubscribeTyping();
    };
  }, [conversation?.id, currentUser?.id]);

  // Position at bottom once when messages first load (like Flutter's reverse ListView)
  // This is a one-time positioning, not auto-scroll
  useEffect(() => {
    if (!loading && messages.length > 0 && messagesContainerRef.current) {
      // Set scroll position to bottom once (no animation, instant)
      const container = messagesContainerRef.current;
      requestAnimationFrame(() => {
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    }
  }, [loading, conversation?.id, messages.length]); // When loading completes, conversation changes, or messages update

  const scrollToBottom = (force = false) => {
    if (!messagesEndRef.current || !messagesContainerRef.current) return;
    
    // Only scroll if forced or user is at bottom
    if (force || isAtBottom) {
      // Use requestAnimationFrame for smoother scrolling
      requestAnimationFrame(() => {
        if (messagesEndRef.current && messagesContainerRef.current) {
          const container = messagesContainerRef.current;
          const scrollHeight = container.scrollHeight;
          const clientHeight = container.clientHeight;
          
          // Smooth scroll to bottom
          container.scrollTo({
            top: scrollHeight - clientHeight,
            behavior: 'smooth'
          });
          
          setIsAtBottom(true);
        }
      });
    }
  };

  // Handle typing indicator - only when user is actually typing
  useEffect(() => {
    if (!currentUser?.id || !conversation.id) return;

    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }

    if (newMessage.trim().length > 0) {
      // User is typing - broadcast immediately
      broadcastTyping(conversation.id, currentUser.id, true);
      
      // Set timeout to stop typing indicator after 2 seconds of no input
      typingTimeoutRef.current = setTimeout(() => {
        broadcastTyping(conversation.id, currentUser.id, false);
        typingTimeoutRef.current = null;
      }, 2000);
    } else {
      // User stopped typing - stop immediately
      broadcastTyping(conversation.id, currentUser.id, false);
    }

    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }
      // Clean up: stop typing when component unmounts or conversation changes
      broadcastTyping(conversation.id, currentUser.id, false);
    };
  }, [newMessage, conversation.id, currentUser?.id]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    const receiverId = conversation.user_id === currentUser?.id 
      ? conversation.seller_id 
      : conversation.user_id;

    const messageContent = newMessage.trim();
    // Only clear on success (prevents flicker/duplicates and lets user retry on failure)

    // Stop typing indicator
    broadcastTyping(conversation.id, currentUser.id, false);
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    try {
    const { data, error } = await sendMessage(conversation.id, receiverId, messageContent);
    
    if (error) {
      alert('Failed to send message: ' + error.message);
    } else if (data) {
      setMessages(prev => {
        if (prev.some(m => m.id === data.id)) return prev;
        const senderUser = resolveUserById(data.sender_id);
        const enriched = senderUser ? { ...data, sender: senderUser } : data;
        const updated = [...prev, enriched];
        cacheMessages(conversation.id, updated);
        lastMessageIdRef.current = data.id || lastMessageIdRef.current;
        return updated;
      });
      
      // Auto-scroll to bottom when user sends a message
      setTimeout(() => {
        if (messagesContainerRef.current) {
          messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
        }
      }, 100);

      setNewMessage('');
    }
    } catch (err) {
      console.error('Error sending message:', err);
    } finally {
    setSending(false);
    }
  };

  const getOtherParty = useCallback(() => {
    if (conversation?.user_id === currentUser?.id) {
      return conversation.seller;
    }
    return conversation?.user;
  }, [conversation?.user_id, conversation?.seller, conversation?.user, currentUser?.id]);

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const chatContent = (
    <div className={`chat-window ${embedded ? 'embedded' : ''} ${isFullscreen ? 'fullscreen' : ''}`} onClick={embedded ? undefined : (e) => e.stopPropagation()}>
        <div className="chat-header">
          <div className="chat-header-info">
            <h3>{getOtherParty()?.username || getOtherParty()?.email || 'Unknown'}</h3>
            {conversation.offer && (
              <div className="offer-info">
                <span className="offer-price">
                  {conversation.offer.currency} {conversation.offer.price}
                </span>
                <span className={`offer-status ${conversation.offer.status}`}>
                  {conversation.offer.status}
                </span>
              </div>
            )}
          </div>
          <div className="chat-header-actions">
            {embedded && (
              <button 
                className="chat-fullscreen-btn" 
                onClick={() => setIsFullscreen(!isFullscreen)}
                title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
              >
                {isFullscreen ? <Minimize2 size={20} /> : <Maximize2 size={20} />}
              </button>
            )}
            <button className="chat-close-btn" onClick={onClose} title="Close chat">
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="chat-messages" ref={messagesContainerRef}>
          {loading && messages.length === 0 ? (
            <div className="chat-loading-skeleton">
              {[1, 2, 3, 4, 5].map(i => (
                <MessageSkeleton key={i} isOwn={i % 2 === 0} />
              ))}
            </div>
          ) : messages.length === 0 ? (
            <div className="chat-empty">No messages yet. Start the conversation!</div>
          ) : (
            <>
              {messages.map((message) => {
                // messages.sender_id is NOT NULL, so authorship cannot distinguish a platform
                // notice from a user message. The prefix is reserved instead: the API rejects it
                // and a trigger on messages blocks anyone but the service role from writing it.
                const systemData = parseSystemMessage(message.content);
                const looksLikeSystem =
                  typeof message.content === 'string' && message.content.trim().startsWith('SYSTEM_MESSAGE:');

                if ((systemData || looksLikeSystem) && conversation?.request_id) {
                  return null;
                }

                if (systemData) {
                  // Render system message
                  const IconComponent = systemData.icon === 'package' ? Package : 
                                      systemData.icon === 'check-circle' ? CheckCircle : Check;
                  
                  return (
                    <div key={message.id} className="chat-message system-message">
                      <div className="system-message-content">
                        <div className="system-message-header">
                          <IconComponent size={18} className="system-message-icon" />
                          <span className="system-message-from">{systemData.from || 'X Market'}</span>
                        </div>
                        <div className="system-message-title">{systemData.title}</div>
                        <div className="system-message-text">{systemData.message}</div>
                        {(systemData.boosting_request_id
                          ? `/boosting/requests/${systemData.boosting_request_id}`
                          : systemData.link && !String(systemData.link).startsWith('/orders/')
                            ? systemData.link
                            : null) && (
                          <Link
                            to={
                              systemData.boosting_request_id
                                ? `/boosting/requests/${systemData.boosting_request_id}`
                                : systemData.link
                            }
                            className="system-message-link"
                          >
                            {systemData.link_label || 'Open request'}
                          </Link>
                        )}
                        <div className="system-message-time">{formatTime(message.created_at)}</div>
                      </div>
                    </div>
                  );
                }

                if (looksLikeSystem) {
                  return (
                    <div key={message.id} className="chat-message system-message">
                      <div className="system-message-content">
                        <div className="system-message-header">
                          <Package size={18} className="system-message-icon" />
                          <span className="system-message-from">X Market</span>
                        </div>
                        <div className="system-message-title">Order update</div>
                        <div className="system-message-time">{formatTime(message.created_at)}</div>
                      </div>
                    </div>
                  );
                }
                
                // Regular message
                const isOwn = message.sender_id === currentUser?.id;
                const senderUser = message.sender || resolveUserById(message.sender_id);
                const senderName = senderUser?.username || senderUser?.email || (message.sender_id ? `User ${String(message.sender_id).slice(0, 6)}` : 'Unknown');
                const shared = parseAdShareMessage(message.content);
                return (
                  <div
                    key={message.id}
                    className={`chat-message ${isOwn ? 'own' : 'other'}`}
                  >
                    {!isOwn && (
                      senderUser?.avatar_url ? (
                        senderUser.avatar_url.startsWith('letter:') ? (
                          <div className="message-avatar-letter">
                            {senderUser.avatar_url.split(':')[1] || (senderName || 'U')[0].toUpperCase()}
                          </div>
                        ) : (
                      <img
                        src={senderUser.avatar_url}
                        alt={senderName}
                        className="message-avatar"
                      />
                        )
                      ) : (
                        <div className="message-avatar-letter">
                          {(senderName || 'U')[0].toUpperCase()}
                        </div>
                      )
                    )}
                    <div className={`message-content${shared ? ' has-ad-share' : ''}`}>
                      {!isOwn && (
                        <div className="message-sender">{senderName}</div>
                      )}
                      {shared ? (
                        <>
                          <AdShareCard share={shared.share} />
                          {shared.text ? (
                            <div className="message-text" style={{ whiteSpace: 'pre-wrap' }}>{shared.text}</div>
                          ) : null}
                        </>
                      ) : (
                        <div className="message-text" style={{ whiteSpace: 'pre-wrap' }}>{message.content}</div>
                      )}
                      <div className="message-time">{formatTime(message.created_at)}</div>
                    </div>
                  </div>
                );
              })}
              {isTyping && (
                <div className="chat-typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              )}
            </>
          )}
          <div ref={messagesEndRef} />
        </div>

        {!conversation?.request_id && !conversation?.ad_id && isConversationChatLocked(order, hasActiveOrder) ? (
          <div className="chat-locked-message">
            <p>This conversation is locked. You can only message when you have an active order with this seller.</p>
          </div>
        ) : (
          <form className="chat-input-form" onSubmit={handleSend}>
            <input
              type="text"
              value={newMessage}
              onChange={(e) => {
                setNewMessage(e.target.value);
                // Prevent scroll when typing
                if (messagesContainerRef.current) {
                  const wasAtBottom = checkIfAtBottom();
                  setIsAtBottom(wasAtBottom);
                }
              }}
              onFocus={() => {
                // Check if at bottom when input is focused
                setIsAtBottom(checkIfAtBottom());
              }}
              placeholder="Type a message..."
              className="chat-input"
              disabled={sending}
            />
            <button
              type="submit"
              className="chat-send-btn"
              disabled={!newMessage.trim() || sending}
            >
              {sending ? <Loader2 size={18} className="spin" /> : 'Send'}
            </button>
          </form>
        )}
      </div>
  );

  if (embedded) {
    return chatContent;
  }

  return (
    <div className="chat-window-overlay" onClick={onClose}>
      {chatContent}
    </div>
  );
};

export default ChatWindow;

